import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Stethoscope, ArrowLeft, Mail, Lock, User, X, ShieldCheck, Smartphone, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { doctorRegistration } from '../../redux/doctorSlice';
import { checkEmailExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import { policyFor, missingAnyContact, policyErrorMessage, policyErrorField } from '../../utils/loginPolicy';
import useDoctorRegistrationOpen from '../../hooks/useDoctorRegistrationOpen';
import baseURL from '../../utils/baseURL';
import { getEmailName } from '../../redux/ctgSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { docPatCreationCountryList, getAllProfessionsList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../../components/common/CountryTimeZoneSelect';
import ProfessionSelect from '../../components/common/ProfessionSelect';
import GenderSelect from '../../components/common/GenderSelect';
import { getGenderOptions, formatFirstName, formatSurname, normalizeEmail, sanitizePhone, PHONE_MAX_LENGTH } from '../../utils/personFields';

// ONE FORM, TWO DOORS ("Concept 2", owner, 1 Oct 2026).
//   - INVITATION: ?invitationCode= — the invitation names the organisation
//     (getEmailName) and the e-mail, which is prefilled and locked.
//   - VOLUNTARY: no code — the organisation is the host's (a tenant-bound
//     host) or picked from those that allow voluntary doctor registration
//     (hooks/useDoctorRegistrationOpen); none = "by invitation only".
// Either way the chosen organisation's LOGIN POLICY decides which contacts
// are mandatory, and every contact GIVEN on a channel the policy marks `otp`
// is proved here with a one-time code: the server requires the proof tokens
// (`emailProof` / `phoneProof`, from /auth/verify-otp) and ignores any
// client-claimed `emailVerified`. The person then signs in normally.

/** One contact proved by a 6-digit code; keeps the proof token. */
function useOtpChannel({ channel, dispatch, axiosInstance, t }) {
  const [sent, setSent] = useState(false);
  const [code, setCodeState] = useState('');
  const [proof, setProof] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const verifyingRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);
  useEffect(() => stopTimer, [stopTimer]);

  const reset = useCallback(() => {
    stopTimer();
    setSent(false);
    setCodeState('');
    setProof('');
    setError('');
    setTimer(0);
    verifyingRef.current = false;
  }, [stopTimer]);

  const send = async (value) => {
    setError('');
    setBusy(true);
    try {
      const result = await dispatch(sendOtp({ [channel]: value, axiosInstance })).unwrap();
      if (result?.success !== false) {
        setSent(true);
        setCodeState('');
        setProof('');
        stopTimer();
        setTimer(60);
        timerRef.current = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) { stopTimer(); return 0; }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : t('doctorRegister.failedToSendOtp'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value, otp) => {
    if (verifyingRef.current || String(otp).length !== 6) return;
    verifyingRef.current = true;
    setError('');
    setBusy(true);
    try {
      const result = await dispatch(verifyOtp({ [channel]: value, otpCode: otp, axiosInstance })).unwrap();
      const token = result?.proof?.token;
      if (token) {
        setProof(token);
        stopTimer();
        setTimer(0);
      } else {
        setError(t('doctorRegister.invalidOtpTryAgain'));
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : t('doctorRegister.invalidOtpTryAgain'));
    } finally {
      verifyingRef.current = false;
      setBusy(false);
    }
  };

  return {
    sent, code, proof, verified: !!proof, error, timer, busy, reset, send, verify,
    setCode: (v) => { setCodeState(String(v).replace(/\D/g, '').slice(0, 6)); setError(''); },
  };
}

export default function DoctorRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['register', 'common']);
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.doctor);
  const { emailCheckLoading } = useSelector((state) => state.auth);
  const { countryTimezoneList, professionListAll, professionListAllLoading } = useSelector((state) => state.master);
  const axiosInstance = useAxiosAuth();

  // Extract invitationCode from URL query parameters using location.search
  const searchParams = new URLSearchParams(location.search);
  const invitationCode = searchParams.get('invitationCode');
  // Country/tenant carried by the invitation. Doctors inherit their tenant's
  // country, so when these are known the form displays the country instead of
  // offering a picker the backend would override anyway.
  const [invitedTenantName, setInvitedTenantName] = useState(null);
  const [invitedTenantId, setInvitedTenantId] = useState(null);
  // THE VOLUNTARY DOOR: the host's organisation, or one picked from those that
  // allow voluntary doctor registration. Not used when an invitation is open.
  const registration = useDoctorRegistrationOpen();
  const hostTenant = !invitationCode && registration.hostTenant?.doctorRegistration === true ? registration.hostTenant : null;
  const [pickedTenantId, setPickedTenantId] = useState('');
  const doorTenantId = invitationCode ? invitedTenantId : (hostTenant?.tenantId || pickedTenantId || null);
  // Read by the debounced e-mail check (an e-mail is free or taken PER
  // organisation), which deliberately keeps no dependencies.
  const doorTenantIdRef = useRef(doorTenantId);
  useEffect(() => { doorTenantIdRef.current = doorTenantId; }, [doorTenantId]);

  // Store dispatch and axiosInstance in refs to avoid dependency issues
  const dispatchRef = useRef(dispatch);
  const axiosInstanceRef = useRef(axiosInstance);

  // Update refs when values change
  useEffect(() => {
    dispatchRef.current = dispatch;
    axiosInstanceRef.current = axiosInstance;
  }, [dispatch, axiosInstance]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [charLimitReached, setCharLimitReached] = useState({});
  const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
  const emailCheckTimeoutRef = useRef(null);
  const lastCheckedEmailRef = useRef(''); // Track last checked email
  const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
  const [isEmailDisabled, setIsEmailDisabled] = useState(false); // Track if email is disabled due to invitation
  const hasCalledInvitationAPIRef = useRef(false); // Track if invitation API has been called

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    countryId: '',
    timeZone: '',
    profession: [],
    gender: '',
    dob: '',
    password: '',
    confirmPassword: '',
  });

  // The ORGANISATION's login policy decides which contacts are mandatory and
  // which must be proved with a code. Invitation door: read by id once the
  // invitation names the tenant. Voluntary door: the rows from
  // useDoctorRegistrationOpen already carry it.
  const [publicTenants, setPublicTenants] = useState([]);
  useEffect(() => {
    if (!invitationCode) return undefined;
    let alive = true;
    (async () => {
      try {
        // Signed out, so /tenants/public answers only the hospital named —
        // the invited one, by id, once the invitation lookup has said which.
        const res = await axiosInstance.get(`${baseURL}/tenants/public`, invitedTenantId ? { params: { tenantId: invitedTenantId } } : undefined);
        if (alive) setPublicTenants(res.data?.tenants || []);
      } catch {
        if (alive) setPublicTenants([]);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedTenantId]);
  const policyRows = invitationCode
    ? publicTenants
    : (hostTenant ? [hostTenant] : registration.tenants);
  const policy = policyFor(policyRows, doorTenantId || '');
  const emailRequired = !!policy.email.required;
  const phoneRequired = !!policy.mobile.required;
  const emailOtpNeeded = !!policy.email.otp;
  const phoneOtpNeeded = !!policy.mobile.otp;

  useEffect(() => {
    dispatch(docPatCreationCountryList());
    dispatch(getAllProfessionsList());
  }, [dispatch]);

  // --- One-time codes, one per channel ---
  const emailOtp = useOtpChannel({ channel: 'email', dispatch, axiosInstance, t });
  const phoneOtp = useOtpChannel({ channel: 'phone', dispatch, axiosInstance, t });
  const resetOtpEmailState = emailOtp.reset;
  const resetOtpPhoneState = phoneOtp.reset;
  const emailOtpVerified = emailOtp.verified;
  const phoneOtpVerified = phoneOtp.verified;
  const handleSendEmailOtp = () => emailOtp.send(formData.email.trim());
  const handleSendPhoneOtp = () => phoneOtp.send(formData.phone.replace(/[^0-9]/g, ''));

  // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
  const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Fetch invitation data if invitationCode is present in URL
  useEffect(() => {
    const fetchInvitationData = async () => {
      // Only call API once using ref check
      if (invitationCode && !hasCalledInvitationAPIRef.current) {
        hasCalledInvitationAPIRef.current = true; // Mark as called immediately

        const resultAction = await dispatch(getEmailName({ invitationCode }));

        if (getEmailName.fulfilled.match(resultAction) && resultAction.payload?.data) {
          const { email, name, tenantName, tenantId } = resultAction.payload.data;

          // The invitation decides the tenant, and the tenant decides the
          // country — the backend derives both, so the form shows the country
          // rather than asking. Older invitations carry no tenant; those fall
          // back to the picker. The tenantId also selects which hospital's
          // login policy shapes this form (phone required or not).
          if (tenantName) setInvitedTenantName(tenantName);
          if (tenantId) setInvitedTenantId(tenantId);

          // Update form data with fetched email and name
          setFormData((prev) => ({
            ...prev,
            email: email || '',
            name: name || '',
          }));

          // Disable email field
          setIsEmailDisabled(true);

          // Trigger email check if email is fetched
          if (email && emailRegex.test(email)) {
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = true;

            const emailCheckAction = await dispatchRef.current(checkEmailExists({
              email: email,
              ...(tenantId ? { tenantId } : {}),
              axiosInstance: axiosInstanceRef.current
            }));

            if (checkEmailExists.fulfilled.match(emailCheckAction)) {
              setEmailStatus(!emailCheckAction.payload.exists);
            }

            isEmailCheckInProgressRef.current = false;
            lastCheckedEmailRef.current = email;
          }
        } else if (getEmailName.rejected.match(resultAction)) {
          toast.warning(t('doctorRegister.invalidOrExpiredInvitationCode'));
        }
      }
    };

    fetchInvitationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationCode]);

  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 8;
    const maxLength = pwd.length <= 25;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    if (!minLength) return t('doctorRegister.passwordMinLength');
    if (!maxLength) return t('validation.password_too_long_25', { ns: 'common' });
    if (!hasUpperCase) return t('doctorRegister.passwordUppercase');
    if (!hasLowerCase) return t('doctorRegister.passwordLowercase');
    if (!hasNumber) return t('doctorRegister.passwordNumber');
    if (!hasSpecialChar) return t('doctorRegister.passwordSpecialChar');
    return '';
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return '';
    const minLength = pwd.length >= 8;
    const maxLength = pwd.length <= 25;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    // Password is strong if it meets all requirements
    if (minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
      return 'Strong';
    }
    return 'Weak';
  };

  // Debounced email check
  useEffect(() => {
    // Clear previous timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    const email = formData.email?.trim() || '';

    // If email is empty or invalid format, reset status
    if (!email || !emailRegex.test(email)) {
      setEmailStatus(null);
      lastCheckedEmailRef.current = '';
      return;
    }

    // If email hasn't changed or check is in progress, don't check again
    if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
      return;
    }

    // Set new timeout for debounced check (500ms delay)
    emailCheckTimeoutRef.current = setTimeout(async () => {
      const trimmedEmail = formData.email.trim();

      // Double check email is still valid and hasn't changed during debounce
      if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
        return;
      }

      // Mark check as in progress and update last checked email BEFORE making the API call
      isEmailCheckInProgressRef.current = true;
      lastCheckedEmailRef.current = trimmedEmail;

      try {
        const resultAction = await dispatchRef.current(checkEmailExists({
          email: trimmedEmail,
          ...(doorTenantIdRef.current ? { tenantId: doorTenantIdRef.current } : {}),
          axiosInstance: axiosInstanceRef.current
        }));

        // Only update status if the email hasn't changed during the API call
        if (formData.email.trim() === trimmedEmail) {
          if (checkEmailExists.fulfilled.match(resultAction)) {
            // exists: true means email already exists (not available)
            // exists: false means email doesn't exist (available)
            setEmailStatus(!resultAction.payload.exists);
          } else {
            setEmailStatus(null);
            lastCheckedEmailRef.current = ''; // Reset on error
          }
        }
      } catch (error) {
        console.error('Error checking email:', error);
        if (formData.email.trim() === trimmedEmail) {
          setEmailStatus(null);
          lastCheckedEmailRef.current = ''; // Reset on error
        }
      } finally {
        isEmailCheckInProgressRef.current = false;
      }
    }, 500);

    // Cleanup function
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    // Use UTC getters for birthDate to avoid timezone shift on DOB
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
      age--;
    }
    return age;
  };

  const validateDob = (value) => {
    if (!value) {
      return t('doctorRegister.dobRequired', 'Date of birth is required');
    }
    const dobDate = new Date(value);
    if (isNaN(dobDate.getTime())) {
      return t('doctorRegister.invalidDateFormat', 'Invalid date format');
    }
    if (dobDate > new Date()) {
      return t('doctorProfile.dob_not_future', { ns: 'common', defaultValue: 'Date of Birth cannot be in the future' });
    }
    if (dobDate.getFullYear() < 1900) {
      return t('doctorProfile.dob_after_1900', { ns: 'common', defaultValue: 'Date of Birth must be after 1900' });
    }
    if (calculateAge(value) < 18) {
      return t('doctorProfile.must_be_18', { ns: 'common', defaultValue: 'Doctor must be at least 18 years old' });
    }
    return '';
  };

  // Max selectable DOB: 18 years before today (HTML-level guard for date pickers)
  const dobMax = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().split('T')[0];
  })();

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = t('doctorRegister.nameRequired');
    } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedName)) {
      newErrors.name = t('doctorRegister.nameInvalidChars');
    } else if (trimmedName.length < 2) {
      newErrors.name = t('doctorRegister.nameMinLength');
    } else if (trimmedName.length > 25) {
      newErrors.name = t('doctorRegister.nameMaxLength');
    } else if (/^\s+$/.test(formData.name)) {
      newErrors.name = t('doctorRegister.nameOnlySpaces');
    }

    // Surname validation
    if (!formData.surname.trim()) {
      newErrors.surname = t('doctorRegister.surnameRequired', 'Surname is required');
    } else if (/^\s+$/.test(formData.surname)) {
      newErrors.surname = t('doctorRegister.surnameOnlySpaces', 'Surname cannot be only spaces');
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = t('doctorRegister.genderRequired', 'Gender is required');
    }

    // Date of Birth validation
    const dobError = validateDob(formData.dob);
    if (dobError) {
      newErrors.dob = dobError;
    }

    // Email validation
    // Organisation (voluntary door only — an invitation names it).
    if (!invitationCode && !doorTenantId) {
      newErrors.tenant = t('doctorRegister.organisation_required', 'Choose the organisation you are joining');
    }

    // Email — mandatory only when the organisation's policy says so.
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      if (emailRequired) newErrors.email = t('doctorRegister.emailRequired');
    } else {
      // Check total length (RFC 5321: max 254 characters)
      if (trimmedEmail.length > 254) {
        newErrors.email = t('doctorRegister.emailTooLong');
      } else {
        // Split email into local and domain parts
        const parts = trimmedEmail.split('@');
        if (parts.length !== 2) {
          newErrors.email = t('doctorRegister.invalidEmailFormat');
        } else {
          const [localPart, domainPart] = parts;
          // Local part (before @) should be max 64 characters (RFC 5321)
          if (localPart.length > 64) {
            newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
          } else if (localPart.length === 0) {
            newErrors.email = t('doctorRegister.invalidEmailFormat');
          } else if (domainPart.length === 0 || !domainPart.includes('.')) {
            newErrors.email = t('doctorRegister.invalidEmailFormat');
          } else if (domainPart.length > 253) {
            newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
          } else if (!emailRegex.test(trimmedEmail)) {
            newErrors.email = t('doctorRegister.invalidEmailFormat');
          } else if (emailStatus === false) {
            newErrors.email = t('doctorRegister.emailAlreadyExists');
          } else if (emailStatus === null && !emailCheckLoading) {
            newErrors.email = t('doctorRegister.pleaseVerifyEmail');
          } else if (emailStatus === true && emailOtpNeeded && !emailOtpVerified) {
            newErrors.email = t('doctorRegister.pleaseVerifyEmailOtp');
          }
        }
      }
    }

    // Mobile number — mandatory only when the policy says so; proved with a
    // code when the policy marks it `otp`.
    const phoneDigits = formData.phone.replace(/[^0-9]/g, '');
    if (phoneRequired && !phoneDigits) {
      newErrors.phone = t('doctorRegister.phoneRequired', 'Mobile number is required');
    } else if (phoneDigits && (phoneDigits.length < 6 || phoneDigits.length > 15)) {
      newErrors.phone = t('doctorRegister.phoneInvalid', 'Enter a valid mobile number (6–15 digits)');
    } else if (phoneDigits && phoneOtpNeeded && !phoneOtpVerified) {
      newErrors.phone = t('doctorRegister.pleaseVerifyPhoneOtp', 'Please verify your mobile number with the code');
    }

    // 'either': neither field is starred, but one of them must be given.
    if (missingAnyContact(policy, { hasEmail: !!trimmedEmail, hasPhone: !!phoneDigits })) {
      newErrors.email = t('loginPolicy.one_contact_required', 'An e-mail or a mobile number is required');
    }

    // Profession validation
    if (!formData.profession || formData.profession.length === 0) {
      newErrors.profession = t('doctorRegister.professionRequired', 'Please select at least 1 profession');
    } else if (formData.profession.length > 3) {
      newErrors.profession = t('doctorRegister.professionMaxExceeded', 'Maximum 3 professions allowed');
    }

    // Country validation
    if (!formData.countryId) {
      newErrors.countryId = t('doctorRegister.countryRequired');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('validation.password_required', { ns: 'common' });
    } else {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('doctorRegister.pleaseConfirmPassword');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('doctorRegister.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLengths = { name: 25, surname: 25 };
    let processedValue = value;

    // First name: only the first character uppercased.
    if (name === 'name' && processedValue.length > 0) {
      processedValue = formatFirstName(processedValue);
    }
    // Surname (family name): first character uppercased, rest as typed.
    if (name === 'surname' && processedValue.length > 0) {
      processedValue = formatSurname(processedValue);
    }

    // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
    if ((name === 'name' || name === 'surname') && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) return;

    // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
    if (name === 'email' && processedValue) {
      // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
      if (!/^[a-zA-Z0-9._+-@]*$/.test(processedValue)) return;
      // Emails are stored and compared lowercase.
      processedValue = normalizeEmail(processedValue);
    }

    // Mobile number: digits only, capped at the international max. A new
    // number needs a new code.
    if (name === 'phone') {
      processedValue = sanitizePhone(processedValue);
      if (processedValue !== formData.phone) resetOtpPhoneState();
    }

    // Prevent leading/trailing spaces for name and surname
    if ((name === 'name' || name === 'surname') && processedValue && (processedValue.startsWith(' ') || processedValue.endsWith(' '))) {
      // Allow the change but will be trimmed on validation
    }

    // Reset email status and tracking when email changes
    if (name === 'email') {
      setEmailStatus(null);
      lastCheckedEmailRef.current = '';
      isEmailCheckInProgressRef.current = false;
      resetOtpEmailState();
    }

    // Check if character limit is reached
    if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
      setCharLimitReached({ ...charLimitReached, [name]: true });
    } else {
      setCharLimitReached({ ...charLimitReached, [name]: false });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Real-time validation for password match
    if (name === 'confirmPassword' && formData.password && value) {
      if (formData.password !== value) {
        setErrors((prev) => ({ ...prev, confirmPassword: t('doctorRegister.passwordsDoNotMatch') }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }

    // Real-time validation for password field when confirmPassword exists
    if (name === 'password' && formData.confirmPassword && value) {
      if (value !== formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: t('doctorRegister.passwordsDoNotMatch') }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  // Submit stays disabled while any inline error is showing — same convention
  // as the login form.
  const hasInlineErrors = Object.values(errors).some(Boolean);
  // Every contact GIVEN on a channel the policy marks `otp` must be proved
  // before Register is possible — the server refuses the form without the
  // proof tokens (OTP_PROOF_REQUIRED).
  const otpOutstanding =
    (!!formData.email.trim() && emailOtpNeeded && !emailOtpVerified) ||
    (!!formData.phone.replace(/[^0-9]/g, '') && phoneOtpNeeded && !phoneOtpVerified);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }



    // A given e-mail must have passed the availability check.
    if (formData.email.trim() && emailStatus !== true) {
      toast.warning(t('doctorRegister.verifyEmailToProceed'));
      return;
    }
    if (otpOutstanding) {
      toast.warning(t('doctorRegister.verify_contacts_first', 'Verify your contact details with the code before registering.'));
      return;
    }

    try {
      // Trim all string fields before submission
      const phoneDigits = formData.phone.replace(/[^0-9]/g, '');
      const trimmedEmail = formData.email.trim();
      const registrationData = {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        ...(phoneDigits ? { phone: phoneDigits } : {}),
        // The proofs the server requires for each mandated-OTP contact given.
        ...(trimmedEmail && emailOtp.proof ? { emailProof: emailOtp.proof } : {}),
        ...(phoneDigits && phoneOtp.proof ? { phoneProof: phoneOtp.proof } : {}),
        countryId: formData.countryId,
        timeZone: formData.timeZone,
        profession: formData.profession.length > 0 ? formData.profession : undefined,
        gender: formData.gender,
        dob: formData.dob,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      // Invitation door: the code (the server takes the tenant from it).
      // Voluntary door: the organisation chosen / given by the host.
      if (invitationCode) {
        registrationData.invitationCode = invitationCode;
      } else if (doorTenantId) {
        registrationData.tenantId = doorTenantId;
      }

      const resultAction = await dispatch(doctorRegistration(registrationData));

      if (doctorRegistration.fulfilled.match(resultAction)) {
        toast.success(t('doctorRegister.registrationSuccess'));
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const data = resultAction.payload?.data || null;
        // The organisation stopped allowing voluntary sign-up meanwhile.
        if (data?.code === 'DOCTOR_REGISTRATION_OFF') {
          toast.warning(t('doctorRegister.registration_off', 'This organisation does not accept doctor registrations. Ask it for an invitation.'));
          return;
        }
        // FIELD_REQUIRED / OTP_PROOF_REQUIRED — on the field, translated. A
        // refused proof means the code must be sent again.
        const policyMsg = policyErrorMessage(data, t, i18n?.language);
        if (policyMsg) {
          const field = policyErrorField(data.field);
          if (data.code === 'OTP_PROOF_REQUIRED') {
            if (field === 'phone') resetOtpPhoneState(); else resetOtpEmailState();
          }
          setErrors((prev) => ({ ...prev, [field]: policyMsg }));
          toast.warning(policyMsg);
          return;
        }
        const errorMessage = data?.message?.[i18n?.language?.startsWith('fr') ? 'fr' : 'en']
          || data?.message?.en
          || t('doctorRegister.registrationFailed');
        toast.warning(errorMessage);
      }
    } catch {
      toast.warning(t('doctorRegister.unexpectedError'));
    }
  };

  // A doctor must belong to an organisation. Without an invitation the page
  // can only offer one that ALLOWS voluntary registration (the host's, or the
  // list); with none, it explains instead of showing a form that can only
  // fail — the backend refuses with 403 DOCTOR_REGISTRATION_OFF.
  if (!invitationCode && registration.loading) {
    return (
      <div data-testid="doctor-register-loading" className="min-h-screen w-full bg-white flex items-center justify-center px-4">
        <span className="w-8 h-8 border-4 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!invitationCode && !registration.open) {
    return (
      <div data-testid="doctor-register-invite-only" className="min-h-screen w-full bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h1 className="text-xl font-bold text-[#1a365d] mb-3">
            {t('doctorRegister.invite_only_title', 'Registration is by invitation only')}
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            {t('doctorRegister.invite_only_text', 'Doctor accounts are created by your organisation. Ask your administrator to send you an invitation link, then open it to complete your registration.')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer"
          >
            {t('doctorRegister.go_to_login', 'Go to login')}
          </button>
        </div>
      </div>
    );
  }

  // A fresh organisation may ask for different contacts and codes, and an
  // e-mail is free or taken per organisation: start the contact checks over.
  const pickTenant = (tenantId) => {
    setPickedTenantId(tenantId);
    setErrors((prev) => ({ ...prev, tenant: '', email: '', phone: '' }));
    resetOtpEmailState();
    resetOtpPhoneState();
    setEmailStatus(null);
    lastCheckedEmailRef.current = '';
    isEmailCheckInProgressRef.current = false;
  };

  const otpPanelCls = 'flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400';
  const otpBtnCls = 'flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

  return (
    <div data-testid="doctor-register-container" className="min-h-screen w-full bg-white overflow-hidden flex items-center justify-center relative py-8 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-20 w-72 h-72 bg-[#4ABDBD]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#3A9B9B]/10 rounded-full blur-3xl"></div>
      </div>

      {/* Back Button - Top Left Corner */}
      <button
        data-testid="doctor-register-back-button"
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 sm:left-8 z-20 px-3 py-2 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#f0faf9] transition flex items-center justify-center gap-2 cursor-pointer"
      >
        <ArrowLeft className="w-5 h-5" />
        {t('doctorRegister.back')}
      </button>

      <div className="relative z-10 w-full max-w-6xl mt-16 sm:mt-8">
        <div >
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Left Column - Intro */}
            <div data-testid="doctor-register-intro-section" className="lg:w-1/3 flex flex-col gap-4 text-center lg:text-left">
              {/* <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-[#4ABDBD] to-[#3A9B9B] flex items-center justify-center shadow-lg">
                <Stethoscope className="text-white w-8 h-8" />
              </div> */}
              <div>
                <h1 data-testid="doctor-register-title" className="gradient-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-2 pb-2">
                  {t('doctorRegister.heading')}
                </h1>
                <p data-testid="doctor-register-description" className="text-gray-500 text-sm mt-2">
                  {t('doctorRegister.description')}
                </p>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:w-2/3">
              <form data-testid="doctor-register-form" onSubmit={handleRegister} className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 0. Organisation — named by the invitation or the host,
                      otherwise chosen among those that accept doctors. */}
                  <div data-testid="doctor-register-tenant-field-container" className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.organisation', 'Organisation')} {!invitationCode && !hostTenant && <span className="text-red-500">*</span>}
                    </label>
                    {invitationCode || hostTenant ? (
                      <div data-testid="doctor-register-tenant-fixed" className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-3 bg-gray-50 text-sm text-gray-700">
                        <Building2 className="text-[#4ABDBD] w-5 h-5 flex-shrink-0" />
                        <span>{(invitationCode ? invitedTenantName : hostTenant?.name) || t('doctorRegister.organisation_from_invitation', 'The organisation that invited you')}</span>
                      </div>
                    ) : (
                      <div className={`relative flex items-center border rounded-xl px-3 py-1 bg-white ${errors.tenant ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'}`}>
                        <Building2 className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                        <select
                          data-testid="doctor-register-tenant-select"
                          value={pickedTenantId}
                          onChange={(e) => pickTenant(e.target.value)}
                          className="tt-select w-full bg-transparent focus:outline-none text-gray-800 text-sm py-2"
                        >
                          <option value="">{t('doctorRegister.organisation_pick', 'Choose an organisation')}</option>
                          {registration.tenants.map((tn) => (
                            <option key={tn.tenantId} value={tn.tenantId}>
                              {[tn.name, tn.city].filter(Boolean).join(' · ')}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {errors.tenant && <p data-testid="doctor-register-tenant-error" className="text-red-500 text-xs mt-1">{errors.tenant}</p>}
                  </div>

                  {/* 1. Gender */}
                  <div data-testid="doctor-register-gender-field-container">
                    <label data-testid="doctor-register-gender-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.gender', 'Gender')} <span className="text-red-500">*</span>
                    </label>
                    <GenderSelect
                      value={formData.gender}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, gender: val }));
                        if (errors.gender) setErrors(prev => ({ ...prev, gender: '' }));
                      }}
                      options={getGenderOptions(t)}
                      placeholder={t('doctorRegister.selectGender', 'Select Gender')}
                      error={!!errors.gender}
                    />
                    {errors.gender && <p data-testid="doctor-register-gender-error" className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                  </div>

                  {/* 2. First Name */}
                  <div data-testid="doctor-register-name-field-container">
                    <label data-testid="doctor-register-name-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.name')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="doctor-register-name-input"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={25}
                        placeholder={t('register.enter_your_name')}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                    </div>
                    {charLimitReached.name && (
                      <p data-testid="doctor-register-name-char-limit" className="text-xs text-amber-600 mt-1">{t('doctorRegister.charLimitReached25')}</p>
                    )}
                    {errors.name && <p data-testid="doctor-register-name-error" className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* 3. Surname */}
                  <div data-testid="doctor-register-surname-field-container">
                    <label data-testid="doctor-register-surname-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.surname', 'Surname')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'}`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="doctor-register-surname-input"
                        type="text"
                        name="surname"
                        value={formData.surname}
                        onChange={handleInputChange}
                        maxLength={25}
                        placeholder={t('doctorRegister.enterSurname', 'Enter surname')}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                    </div>
                    {errors.surname && <p data-testid="doctor-register-surname-error" className="text-red-500 text-xs mt-1">{errors.surname}</p>}
                  </div>

                  {/* 4. Date of Birth */}
                  <div data-testid="doctor-register-dob-field-container">
                    <label data-testid="doctor-register-dob-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.dob', 'Date of Birth')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      data-testid="doctor-register-dob-input"
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, dob: value }));
                        // Instant validation: surface 18+/future/range errors as the user picks a date
                        const dobErr = value ? validateDob(value) : '';
                        setErrors(prev => ({ ...prev, dob: dobErr }));
                      }}
                      min="1900-01-01"
                      max={dobMax}
                      className={`w-full px-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-200'} ${!formData.dob ? 'text-gray-400' : 'text-gray-800'}`}
                    />
                    {errors.dob && <p data-testid="doctor-register-dob-error" className="text-red-500 text-xs mt-1">{errors.dob}</p>}
                  </div>

                  {/* Profession */}
                  <div data-testid="doctor-register-profession-field-container">
                    <label data-testid="doctor-register-profession-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.profession', 'Profession')} <span className="text-red-500">*</span>
                    </label>
                    <ProfessionSelect
                      value={formData.profession}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, profession: val }));
                        if (errors.profession) setErrors(prev => ({ ...prev, profession: '' }));
                      }}
                      professions={professionListAll || []}
                      loading={professionListAllLoading}
                      placeholder={t('doctorRegister.selectProfession', 'Select Profession')}
                      error={!!errors.profession}
                    />
                    {errors.profession && <p data-testid="doctor-register-profession-error" className="text-red-500 text-xs mt-1">{errors.profession}</p>}
                  </div>

                  {/* Email */}
                  <div data-testid="doctor-register-email-field-container">
                    <label data-testid="doctor-register-email-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.email')}{' '}
                      {emailRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs font-normal">({t('common.optional', { ns: 'common', defaultValue: 'optional' })})</span>}
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${emailOtpVerified
                      ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                      : emailStatus === false
                        ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                        : emailStatus === true
                          ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                          : errors.email
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                      <input
                        data-testid="doctor-register-email-input"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('register.enter_your_email')}
                        maxLength={254}
                        disabled={isEmailDisabled || emailOtpVerified}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      {emailCheckLoading && (
                        <span data-testid="doctor-register-email-loading" className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                      )}
                      {!emailCheckLoading && emailStatus === false && (
                        <X
                          data-testid="doctor-register-email-clear-icon"
                          className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, email: '' }));
                            setEmailStatus(null);
                            setErrors(prev => ({ ...prev, email: '' }));
                            lastCheckedEmailRef.current = '';
                            resetOtpEmailState();
                          }}
                        />
                      )}
                      {!emailCheckLoading && emailStatus === true && emailOtpNeeded && !emailOtpVerified && (
                        <button
                          data-testid="doctor-register-send-otp-button"
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={emailOtp.busy || emailOtp.sent}
                          className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {emailOtp.busy && !emailOtp.sent ? '...' : emailOtp.sent ? t('doctorRegister.sent') : t('doctorRegister.sendOtp')}
                        </button>
                      )}
                      {emailOtpVerified && (
                        <ShieldCheck data-testid="doctor-register-email-verified-icon" className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    {emailOtpVerified && (
                      <p data-testid="doctor-register-otp-verified-text" className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('doctorRegister.emailOtpVerified')}</p>
                    )}
                    {emailOtp.sent && !emailOtpVerified && (
                      <div data-testid="doctor-register-otp-section" className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="doctor-register-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={emailOtp.code}
                            onChange={(e) => emailOtp.setCode(e.target.value)}
                            placeholder={t('doctorRegister.enterOtpPlaceholder')}
                            className={otpPanelCls}
                          />
                          <button
                            data-testid="doctor-register-verify-otp-button"
                            type="button"
                            onClick={() => emailOtp.verify(formData.email.trim(), emailOtp.code)}
                            disabled={emailOtp.busy || emailOtp.code.length !== 6}
                            className={otpBtnCls}
                          >
                            {emailOtp.busy ? '...' : t('doctorRegister.verify')}
                          </button>
                        </div>
                        <div>
                          {emailOtp.timer > 0 ? (
                            <span data-testid="doctor-register-otp-timer" className="text-xs text-gray-500">{t('doctorRegister.resendIn', { timer: emailOtp.timer })}</span>
                          ) : (
                            <button data-testid="doctor-register-resend-otp-button" type="button" onClick={handleSendEmailOtp} disabled={emailOtp.busy} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">{t('doctorRegister.resendOtp')}</button>
                          )}
                        </div>
                        {emailOtp.error && <p data-testid="doctor-register-otp-error" className="text-red-500 text-xs">{emailOtp.error}</p>}
                      </div>
                    )}
                    {emailOtp.error && !emailOtp.sent && <p data-testid="doctor-register-otp-error" className="text-red-500 text-xs mt-1">{emailOtp.error}</p>}
                    {errors.email && <p data-testid="doctor-register-email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    {!emailCheckLoading && emailStatus === false && !errors.email && (
                      <p data-testid="doctor-register-email-exists-error" className="text-red-500 text-xs mt-1">{t('doctorRegister.emailAlreadyExist')}</p>
                    )}
                  </div>

                  {/* Mobile number — mandatory when the organisation's policy
                      says so, proved with a code when it marks it `otp`. */}
                  <div data-testid="doctor-register-phone-field-container">
                    <label data-testid="doctor-register-phone-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.phone', 'Mobile number')}{' '}
                      {phoneRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs font-normal">({t('common.optional', { ns: 'common', defaultValue: 'optional' })})</span>}
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${phoneOtpVerified ? 'border-green-400' : errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'}`}>
                      <Smartphone className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                      <input
                        data-testid="doctor-register-phone-input"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t('doctorRegister.phonePlaceholder', 'Mobile number (digits only)')}
                        maxLength={PHONE_MAX_LENGTH}
                        disabled={phoneOtpVerified}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        autoComplete="tel"
                      />
                      {phoneOtpNeeded && !phoneOtpVerified && /^\d{6,15}$/.test(formData.phone) && (
                        <button
                          data-testid="doctor-register-phone-send-otp-button"
                          type="button"
                          onClick={handleSendPhoneOtp}
                          disabled={phoneOtp.busy || phoneOtp.sent}
                          className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {phoneOtp.busy && !phoneOtp.sent ? '...' : phoneOtp.sent ? t('doctorRegister.sent') : t('doctorRegister.sendOtp')}
                        </button>
                      )}
                      {phoneOtpVerified && (
                        <ShieldCheck data-testid="doctor-register-phone-verified-icon" className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    {phoneOtpVerified && (
                      <p data-testid="doctor-register-phone-otp-verified-text" className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('doctorRegister.phoneOtpVerified', 'Mobile number verified')}</p>
                    )}
                    {phoneOtp.sent && !phoneOtpVerified && (
                      <div data-testid="doctor-register-phone-otp-section" className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="doctor-register-phone-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={phoneOtp.code}
                            onChange={(e) => phoneOtp.setCode(e.target.value)}
                            placeholder={t('doctorRegister.enterOtpPlaceholder')}
                            className={otpPanelCls}
                          />
                          <button
                            data-testid="doctor-register-phone-verify-otp-button"
                            type="button"
                            onClick={() => phoneOtp.verify(formData.phone.replace(/[^0-9]/g, ''), phoneOtp.code)}
                            disabled={phoneOtp.busy || phoneOtp.code.length !== 6}
                            className={otpBtnCls}
                          >
                            {phoneOtp.busy ? '...' : t('doctorRegister.verify')}
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          {phoneOtp.timer > 0 ? (
                            <span data-testid="doctor-register-phone-otp-timer" className="text-xs text-gray-500">{t('doctorRegister.resendIn', { timer: phoneOtp.timer })}</span>
                          ) : (
                            <button data-testid="doctor-register-phone-resend-otp-button" type="button" onClick={handleSendPhoneOtp} disabled={phoneOtp.busy} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">{t('doctorRegister.resendOtp')}</button>
                          )}
                        </div>
                        {phoneOtp.error && <p data-testid="doctor-register-phone-otp-error" className="text-red-500 text-xs">{phoneOtp.error}</p>}
                      </div>
                    )}
                    {phoneOtp.error && !phoneOtp.sent && <p data-testid="doctor-register-phone-otp-error" className="text-red-500 text-xs mt-1">{phoneOtp.error}</p>}
                    {errors.phone && <p data-testid="doctor-register-phone-error" className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    {phoneRequired && !errors.phone && !phoneOtp.sent && (
                      <p className="text-gray-400 text-xs mt-1">{t('doctorRegister.phoneLoginHint', 'You will use this number to sign in.')}</p>
                    )}
                  </div>

                  {/* Country & Timezone */}
                  <div data-testid="doctor-register-country-field-container" className="md:col-span-2">
                    <label data-testid="doctor-register-country-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.country')} <span className="text-red-500">*</span>
                    </label>
                    <CountryTimeZoneSelect
                      countryTimezoneList={countryTimezoneList}
                      countryId={formData.countryId}
                      timeZone={formData.timeZone}
                      onChange={({ countryId, timeZone }) => {
                          setFormData(prev => ({ ...prev, countryId, timeZone }));
                          setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                      }}
                      placeholder={t('doctorRegister.selectCountry', 'Select Country')}
                      error={!!errors.countryId || !!errors.timeZone}
                    />
                    {errors.countryId && <p data-testid="doctor-register-country-error" className="text-red-500 text-xs mt-1">{errors.countryId}</p>}
                    {errors.timeZone && <p data-testid="doctor-register-timezone-error" className="text-red-500 text-xs mt-1">{errors.timeZone}</p>}
                  </div>

                  {/* Password */}
                  <div data-testid="doctor-register-password-field-container">
                    <label data-testid="doctor-register-password-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.password')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Lock className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="doctor-register-password-input"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('register.create_password')}
                        maxLength={25}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                      <button
                        data-testid="doctor-register-toggle-password-button"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="ml-2 focus:outline-none hover:scale-110 transition-transform"
                      >
                        {showPassword ? (
                          <Eye className="text-[#4ABDBD] w-5 h-5" />
                        ) : (
                          <EyeOff className="text-[#4ABDBD] w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && <p data-testid="doctor-register-password-error" className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    {formData.password && !errors.password && (
                      <p data-testid="doctor-register-password-strength-text" className={`text-xs mt-1 ${getPasswordStrength(formData.password) === 'Strong'
                        ? 'text-green-600'
                        : 'text-amber-600'
                        }`}>
                        {getPasswordStrength(formData.password) === 'Strong'
                          ? t('doctorRegister.passwordStrengthStrong')
                          : t('doctorRegister.passwordStrengthWeak')}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div data-testid="doctor-register-confirm-password-field-container">
                    <label data-testid="doctor-register-confirm-password-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('doctorRegister.confirmPassword')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Lock className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="doctor-register-confirm-password-input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder={t('register.confirm_password')}
                        maxLength={25}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                      <button
                        data-testid="doctor-register-toggle-confirm-password-button"
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="ml-2 focus:outline-none hover:scale-110 transition-transform"
                      >
                        {showConfirmPassword ? (
                          <Eye className="text-[#4ABDBD] w-5 h-5" />
                        ) : (
                          <EyeOff className="text-[#4ABDBD] w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && <p data-testid="doctor-register-confirm-password-error" className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>

                <button
                  data-testid="doctor-register-submit-button"
                  type="submit"
                  disabled={loading || hasInlineErrors || otpOutstanding}
                  className={`w-full py-3 sm:py-4 font-bold rounded-xl text-white bg-linear-to-r from-[#4ABDBD] to-[#3A9B9B] hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${(loading || hasInlineErrors || otpOutstanding) ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.01]'
                    }`}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      {t('doctorRegister.registering')}
                    </>
                  ) : (
                    <>
                      <Stethoscope className="w-5 h-5" />
                      {t('doctorRegister.register')}
                    </>
                  )}
                </button>

                {otpOutstanding && (
                  <p data-testid="doctor-register-otp-outstanding" className="-mt-3 text-center text-xs text-gray-500">
                    {t('doctorRegister.verify_contacts_first', 'Verify your contact details with the code before registering.')}
                  </p>
                )}

                <p data-testid="doctor-register-already-have-account-text" className="text-center text-sm text-gray-600">
                  {t('doctorRegister.alreadyHaveAccount')}{' '}
                  <button data-testid="doctor-register-login-button" type="button" onClick={() => navigate('/login')} className="text-[#4ABDBD] font-semibold hover:underline">
                    {t('doctorRegister.login')}
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
